// frontend/src/lib/auth.ts
import { api, apiJson } from '@/client'

export type Identity = {
  user: any | null
  tenant: string
  roles: string[]
  claims?: Record<string, any>
}

/** Simple dev check */
const isDev =
  (import.meta as any)?.env?.MODE === 'development' ||
  (typeof location !== 'undefined' &&
    (location.hostname === 'localhost' || location.hostname === '127.0.0.1'))

/**
 * Optional: pull a bearer token from your OIDC library or localStorage.
 * Replace this with your real token getter when OIDC is wired.
 */
export function getAccessToken(): string | undefined {
  // e.g. return (window as any).__oidc?.getAccessToken?.()
  // e.g. return localStorage.getItem('access_token') || undefined
  return undefined
}

/** Normalize arbitrary backend shape into our Identity */
function normalize(me: any): Identity {
  const roles = Array.isArray(me?.roles) ? me.roles.map(String) : []
  return {
    user:   me?.user ?? null,
    tenant: (me?.tenant ?? 'public') as string,
    roles,
    claims: (me?.claims ?? {}) as Record<string, any>,
  }
}

/**
 * Fetch the current identity from the API.
 * - Uses Authorization: Bearer <token> if available
 * - In **dev**, guarantees roles include "admin" so RBAC-gated pages render
 * - Never throws; always returns a usable Identity
 */
export async function fetchIdentity(token?: string): Promise<Identity> {
  const bearer = token ?? getAccessToken()
  try {
    const headers: Record<string, string> = {}
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`

    // NOTE: client.ts prefixes /v1 automatically; no /v1 here
    const me = await apiJson<Identity>('/auth/me', { headers }, false)
    let id = normalize(me)

    // DEV OVERRIDE: ensure admin for local demo, even on 200 OK
    if (isDev && !id.roles.includes('admin')) {
      id = { ...id, roles: [...id.roles, 'admin'] }
    }
    return id
  } catch {
    // On network/parse error, still allow admin in dev
    if (isDev) {
      return { user: { name: 'Dev User' }, tenant: 'public', roles: ['admin'], claims: {} }
    }
    // Production fallback: minimal anonymous identity
    return { user: null, tenant: 'public', roles: [], claims: {} }
  }
}

/** Helper for guards */
export function hasRole(identity: Identity | null | undefined, ...need: string[]): boolean {
  if (!identity) return false
  const roles = identity.roles ?? []
  return need.every(r => roles.includes(r))
}

export async function revokeSession(): Promise<void> {
  const bearer = getAccessToken()
  try {
    const headers: Record<string, string> = {}
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`
    await api('/auth/revoke', { method: 'POST', headers })
  } catch {
    // swallow in dev; surface errors later if desired
  }
}